/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
//import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
//import { IncrementalQuantity, ITelemetryContent, Module, Node, Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxProps as IBuyboxExtentionProps,
    IBuyboxResources as IBuyboxExtentionResources
} from '../definition-extensions/buybox.ext.props.autogenerated';

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        max,
        shopSimilarDescription,
        unitOfMeasure
    } = props;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {price}
                {unitOfMeasure}
                {props.bulkPurchaseLink}
                {description}
                {_renderTextBlock(props.slots.textBlocks)}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity && _renderQuantity(quantity, callbacks, props, state, resources, quantityLimitsMessages, max, telemetryContent)}
                {inventoryLabel}
                {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist, props)}
                {findInStore && _renderFindInStore(findInStore)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        </Module>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps,
    props?: IBuyboxProps<IBuyboxData>
): JSX.Element | undefined => {
    const [isChecked, setIsChecked] = React.useState(true);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    React.useEffect(() => {
        sessionStorage.setItem('isChargerRequired', 'true');
    }, []);

    const onChange = () => {
        setIsChecked((prevChecked) => {
            const newChecked = !prevChecked;
    
            // Update sessionStorage depending on the new value of isChecked
            sessionStorage.setItem('isChargerRequired', newChecked ? 'true' : 'false');
    
            if (!newChecked && !isModalOpen) {
                // When isChecked becomes false and modal is not open
                setIsModalOpen(true);
            }
    
            return newChecked; // Update the state correctly
        });
    };
    //AVA  TO DO Update the actual content once 50 sales completed
    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='charger-installation' style={{background:'#f5f5f5',padding:'24px'}}>                            
                <div className='charger-installation-title' style={{fontWeight:'bold',fontSize:'16px',marginBottom:'12px'}}>Free Installation Estimate</div>                                                          
                <div><input
                    // className='ms-checkout-terms-and-conditions__input-checkbox'
                    className='ms-checkout-terms-and-conditions__input-checkbox'
                    type='checkbox'
                    aria-label='installation-checkbox'
                    aria-checked={isChecked}
                    name='checkoutTermsAndConditions'
                    onChange={onChange}
                    disabled={false}
                    checked={isChecked}
                    id='installation-checkbox'
                />
                <span className='charger-installation-description' style={{fontSize:'16px',margin:'12px 13px 12px 8px'}}>Want a safe and seamless home EV charger installation done by Ampol?</span>  
                </div>              
                <div className='charger-installation-description' style={{fontSize:'12px',margin:'12px 0 0'}}>
                <p style={{marginBottom:'12px'}}>Get a free and non-binding installation estimate from us.</p>  
                <p style={{marginBottom:'12px'}}>Once your order is placed, simply complete the Installation Request Form via email to determine the works and costs required for your installation.</p>              
                
                <p>Here's an overview of what's included in every Standard Installation done by us</p>
                    <ul>
                        <li>Installation and testing of an AmpCharge EV Charger 7kW configured for single phase charging.</li>
                        <li>Up to 20 meters of wall/ceiling mounted cable included.</li>
                        <li>Supply and install of safety switch and circuit breaker in a location that is easily accessible near the EV Charger.</li>
                        <li>Installation in accordance with relevant Australian standards.</li>
                        <li>All associated electrical containment and hardware fixings for the installation.</li>                        
                    </ul>
                    <p>Note: Our Standard Installation is done at a fixed fee. Any additional work that exceeds the scope of a Standard Installation will be at extra cost. Your installation estimate will list the work required and associated costs. All estimates are free and non-binding – you can then decide if you want to go ahead or not.</p>
                    <p style={{marginBottom:'12px',marginTop:'12px'}}>Please visit our <a href='https://ampcharge.ampol.com.au/charging-at-home/installation' style={{textDecoration:'underline'}}>installation page</a> to find out why you should choose our Ampol installation service.
                    </p>                
                </div>
            </div>
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && addToOrderTemplate.button}
                {addToWishlist && addToWishlist.button}
            </div>            
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={max}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
